"use client";
import { MAX_CART_ITEM_QTY } from "@repo/constants/cart";
import { CartContext } from "@repo/contexts/CartContext";
import { useSnackbarHandler } from "@repo/lib/hooks/common";
import { useTrackCartUpdated } from "@repo/lib/hooks/webengage";
import { updateItemQuantity } from "@repo/server-actions/cart";
import { LoadingDots } from "@repo/ui";
import cx from "classnames";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useContext, useTransition } from "react";

const MIN_QTY = 1;

function SubmitButton({ type, onClick, pending, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        type === "plus" ? "Increase item quantity" : "Reduce item quantity"
      }
      aria-disabled={pending || disabled}
      className={cx(
        "ease border-miniCart-cartItems-quantityButtonBorder text-miniCart-cartItems-quanttityButtonIcon bg-miniCart-cartItems-quantityButtonBg hover:bg-miniCart-cartItems-quantityButtonBg h-9 min-w-[42px] max-w-[42px] border-x text-2xl transition-all duration-200 md:h-11",
        {
          "flex cursor-not-allowed items-center justify-center":
            pending || disabled,
        }
      )}
      disabled={pending || disabled}
    >
      {pending ? (
        <LoadingDots className="bg-black" />
      ) : type === "plus" ? (
        <span>+</span>
      ) : (
        <span>-</span>
      )}
    </button>
  );
}

const EditQuantityButton = ({ item, type }) => {
  const locale = useLocale();
  const t = useTranslations("Common");
  const [isPending, startTransition] = useTransition();
  const { setCartTotalsInfo } = useContext(CartContext);
  const { showSnackbar } = useSnackbarHandler();
  const { id, quantity } = item;
  const { trackCartUpdatedEvent } = useTrackCartUpdated();

  const handleSubmit = useCallback(async () => {
    if (type === "plus" && quantity >= MAX_CART_ITEM_QTY) {
      showSnackbar({
        type: "error",
        message: t("Maximum_Quantity_Is_Exceeded"),
      });
      return;
    }
    startTransition(async () => {
      const newQuantity = type === "plus" ? quantity + 1 : quantity - 1;
      const payload = {
        id,
        quantity: newQuantity,
      };
      const response = await updateItemQuantity(payload, locale);
      if (response?.success) {
        setCartTotalsInfo(response);
        trackCartUpdatedEvent(response);
      }
      showSnackbar(response);
    });
  }, [
    id,
    type,
    quantity,
    setCartTotalsInfo,
    locale,
    showSnackbar,
    trackCartUpdatedEvent,
    t,
  ]);

  const isDisabled = type === "minus" && quantity <= MIN_QTY;

  return (
    <>
      <SubmitButton
        type={type}
        onClick={handleSubmit}
        pending={isPending}
        disabled={isDisabled}
      />
    </>
  );
};

export default EditQuantityButton;
